/**
 * Campaign API helpers (resource: `campaign`).
 *
 * The backend shape:
 *   - `audience_filters` is a list of `{column, operator, value}` rows
 *   - `delivery_methods` is an ordered list of {sequence, method, receivers, label, enabled}
 *   - `delivery_reactions` is an ordered list of reaction rows (advanced strategy)
 *   - `attachments` is an M2M to Attachment (nested ``{id, name, size, url}``
 *     on read, list of ids on write under `attachment_ids`)
 *
 * These helpers centralise the JSON shape + normalisation so both the list
 * grid and the editor agree on defaults.
 */

import { CREATE, DELETE, GET_LIST, GET_ONE, UPDATE } from '../api/dataProvider';

// Static fallback shape for the filter registry so the UI never ends up with a
// fully-disabled filter builder when the backend endpoint is unreachable.
// Mirrors the keys returned by `filter_options/` so consumers can read
// `columns`, `main_office_columns`, and `channel_columns` without checking
// for undefined.
const EMPTY_FILTER_REGISTRY = {
    columns: [],
    main_office_columns: [],
    channel_columns: [],
    categories: [],
    statuses: [],
    delivery_strategies: [],
    delivery_methods: [],
    send_as_options: [],
};

export const CAMPAIGN_RESOURCE = 'campaign';

function getBootstrapUserPermissions() {
    try {
        const raw = window.localStorage.getItem('bootstrap');
        if (!raw) return null;
        return JSON.parse(raw)?.user?.permissions ?? null;
    } catch {
        return null;
    }
}

/**
 * Campaigns UI is gated on `campaign.list`, its own RBAC scope. It used to share
 * `notification.list` with the rest of the Notifications section, which meant a
 * group allowed to run campaigns was necessarily allowed to edit notification
 * templates too (backend users migration 0132 split the scopes and cloned the
 * existing grants). This mirrors `ContextStore.hasPerm`, but reads straight from
 * the bootstrap blob so it can run outside React (route guards, sidebar) without
 * threading context through. Defaults to allow when there's no permissions blob
 * or access groups are disabled, matching `hasPerm`.
 */
export function userCanAccessCampaignsUi() {
    const up = getBootstrapUserPermissions();
    if (!up) return true;
    if (!up.access_groups_enabled) return true;
    if (up.full_access) return true;
    if (up.scoped_all_tags?.campaign?.list) return true;
    if (up.perms?.campaign?.list) return true;
    return false;
}

// Kept in sync with Campaign.STATUS_CHOICES on the backend.
export const CAMPAIGN_STATUS = {
    DRAFT: 'draft',
    SCHEDULED: 'scheduled',
    ONGOING: 'ongoing',
    SENT: 'sent',
    FAILED: 'failed',
};

export const CAMPAIGN_READ_ONLY_STATUSES = [CAMPAIGN_STATUS.ONGOING, CAMPAIGN_STATUS.SENT];

export const CAMPAIGN_TIMING = {
    NOW: 'now',
    SCHEDULED: 'scheduled',
};

/**
 * Browser IANA zone used for campaign schedule pick/display. The picker is
 * ``datetime-local`` (naive); we treat it as this zone and send UTC ISO to
 * the API. Not UTC and not the customer's timezone.
 */
export function getOperatorTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
}

/** e.g. ``local time (America/New_York)`` — always names the zone when known. */
export function operatorLocalTimeLabel(base = 'local time') {
    const tz = getOperatorTimeZone();
    return tz ? `${base} (${tz})` : base;
}

export const CAMPAIGN_STRATEGY = {
    STOP_FIRST: 'stop_first',
    USE_ALL: 'use_all',
    ADVANCED: 'advanced',
};

// Kept in sync with Campaign.SEND_AS_CHOICES on the backend. A campaign now
// targets exactly one chat channel — picking, say, "announcement" also
// determines that the audience tab uses the channel-mode filter builder.
export const SEND_AS = {
    PRIVATE_MESSAGE: 'private_message',
    ANNOUNCEMENT: 'announcement',
    MAIN_OFFICE: 'main_office',
};

// Each `send_as` value maps to exactly one audience surface. We keep the
// mapping centralized here (and mirror it on the backend in
// `apiv2.views.edit.campaign.CampaignViewset._mode_for_send_as`) so the
// audience builder, the preview API call, and the backend filter
// validation all agree on which column registry / preview shape applies.
export const AUDIENCE_MODE = {
    USER: 'user',          // private_message    -> per-GTU filters
    MAIN_OFFICE: 'main_office', // main_office   -> TUBC-on-MO filters
    CHANNEL: 'channel',    // announcement       -> per-channel filters
};

export function audienceModeForSendAs(sendAs) {
    if (sendAs === SEND_AS.ANNOUNCEMENT) return AUDIENCE_MODE.CHANNEL;
    if (sendAs === SEND_AS.MAIN_OFFICE) return AUDIENCE_MODE.MAIN_OFFICE;
    return AUDIENCE_MODE.USER;
}

// Kept in sync with Campaign.PURPOSE_CHOICES on the backend. Marketing
// campaigns get gated by User.allow_ads for paid channels (email);
// transactional campaigns bypass the gate.
export const CAMPAIGN_PURPOSE = {
    TRANSACTIONAL: 'transactional',
    MARKETING: 'marketing',
};

/**
 * What the campaign editor may offer this operator: ``{promotional}``. Promotional campaigns come back operator by
 * operator (backend rollout phase 4, ``config["campaign_promotional_enabled"]``); the backend refuses to send them
 * where the switch is off, so this only keeps the editor coherent. Any failure, or an older backend (404), reads as
 * off.
 */
export async function fetchCampaignFeatures(dataAPI) {
    try {
        const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, { urlSuffix: 'features/' });
        if (res?.statusCode >= 400) return { promotional: false };
        return { promotional: res?.data?.promotional === true };
    } catch (err) {
        console.warn('fetchCampaignFeatures failed', err);
        return { promotional: false };
    }
}

// Kept in sync with demo.lib.events.constants. Only CHAT and EMAIL are
// surfaced in the editor for initial launch; SMS/WhatsApp/Push exist on
// the backend but are hidden from the channel picker until cost-control
// guardrails (entitlement flag, cost preflight, daily caps) ship.
export const DELIVERY_METHOD = {
    EMAIL: 0,
    SMS: 1,
    CHAT: 2,
    PUSH_NOTIFICATION: 3,
    WHATSAPP: 7,
    AIRBNB: 8,
    // Composite methods: a single operator-facing checkbox that the
    // backend resolves to one of the channels above per recipient at
    // send time. Mirror of ``demo.lib.events.constants`` and the
    // ``Campaign_Delivery_Method`` model.
    MOBILE_PHONE_NUMBER: 9,
    CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER: 10,
};

function toNum(v) {
    if (v == null || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function asInt(v) {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

function fkId(x) {
    if (x == null) return null;
    if (typeof x === 'object' && 'id' in x) return x.id ?? null;
    return x;
}

function normalizeAtagList(atag) {
    if (!Array.isArray(atag)) return [];
    return atag.map(fkId).filter((v) => v != null);
}

// Per-method body fields persisted on ``Campaign_Delivery_Method``.
// Mirrors the ``fields`` list on ``CampaignDeliveryMethodSerializer`` —
// every field the backend accepts/returns *besides* the structural ones
// (id, sequence, method, receivers, label, enabled) must round-trip
// through normalize, or it silently clears on save. (Symptom: operator
// types an email subject + body, clicks Save, fields go blank — because
// ``toApiPayload`` re-normalizes and drops anything not listed here.)
const _DM_PER_METHOD_FIELDS = [
    'email_subject',
    'email_message',
    'email_message_text',
    'sms_message',
    'whatsapp_message',
    'chat_message',
    'airbnb_message',
    'push_title',
    'push_body',
];

function normalizeDeliveryMethods(items) {
    if (!Array.isArray(items)) return [];
    return items
        .map((it, idx) => {
            const row = {
                id: it.id ?? null,
                sequence: asInt(it.sequence) ?? idx,
                method: asInt(it.method),
                receivers: it.receivers || 'user',
                label: it.label ?? '',
                enabled: it.enabled !== false,
            };
            for (const f of _DM_PER_METHOD_FIELDS) {
                if (it[f] !== undefined && it[f] !== null) {
                    row[f] = it[f];
                }
            }
            return row;
        })
        .sort((a, b) => a.sequence - b.sequence);
}

function normalizeAudienceFilters(filters) {
    if (!Array.isArray(filters)) return [];
    return filters
        .filter((f) => f && f.column && f.operator)
        .map((f) => ({ column: f.column, operator: f.operator, value: f.value ?? null }));
}

// Channel-mode filters use the same row shape as user-mode but a different
// column registry on the backend (`apiv2/lib/campaign_channel_filters.py`).
// The normalize/payload code is identical aside from the field name.
function normalizeChannelAudienceFilters(filters) {
    return normalizeAudienceFilters(filters);
}

function normalizeDeliveryReactions(reactions) {
    if (!Array.isArray(reactions)) return [];
    return reactions;
}

export function normalizeCampaignRow(row) {
    if (!row) return row;
    const sendAs = row.send_as && Object.values(SEND_AS).includes(row.send_as)
        ? row.send_as
        : SEND_AS.PRIVATE_MESSAGE;
    return {
        ...row,
        opened_pct: toNum(row.opened_pct),
        clicked_pct: toNum(row.clicked_pct),
        send_as: sendAs,
        audience_filters: normalizeAudienceFilters(row.audience_filters),
        audience_channel_filters: normalizeChannelAudienceFilters(row.audience_channel_filters),
        delivery_reactions: normalizeDeliveryReactions(row.delivery_reactions),
        delivery_methods: normalizeDeliveryMethods(row.delivery_methods),
        purpose: Object.values(CAMPAIGN_PURPOSE).includes(row.purpose)
            ? row.purpose
            : CAMPAIGN_PURPOSE.MARKETING,
        // Default true = historical behavior (cancelled guests included).
        // Only honored for Promotional; Essential always excludes them
        // (enforced backend-side regardless of this value).
        include_cancelled_bookings: row.include_cancelled_bookings !== false,
        translate_to_recipient_lang: row.translate_to_recipient_lang === true,
        translations: row.translations && typeof row.translations === 'object'
            ? row.translations
            : {},
        // Backend serializes attachments as a list of ``{id, name, size,
        // url}`` nested objects so the dashboard picker can render
        // thumbnails without a second round-trip; ``size`` (bytes, 0/null =
        // unknown) feeds the oversized-media warning for the mobile
        // channels. Outgoing payloads strip everything but the id and POST
        // ``attachment_ids`` (list of ints) — see ``toApiPayload`` below.
        attachments: Array.isArray(row.attachments)
            ? row.attachments
                .map((a) => ({
                    id: a?.id ?? null,
                    url: a?.url || null,
                    name: a?.name || '',
                    size: Number.isFinite(a?.size) && a.size > 0 ? a.size : null,
                }))
                .filter((a) => a.id != null)
            : [],
        template: fkId(row.template),
        template_name: row.template_name || null,
        parent_campaign: fkId(row.parent_campaign),
        ruleset: fkId(row.ruleset),
        ruleset_name: row.ruleset_name || null,
        atag: normalizeAtagList(row.atag),
    };
}

/** Outgoing body for POST/PUT. Mirrors the serializer fields. */
export function toApiPayload(record) {
    const sendAs = Object.values(SEND_AS).includes(record.send_as)
        ? record.send_as
        : SEND_AS.PRIVATE_MESSAGE;
    return {
        name: (record.name ?? '').trim(),
        category: record.category || null,
        status: record.status || CAMPAIGN_STATUS.DRAFT,
        send_timing: record.send_timing || CAMPAIGN_TIMING.NOW,
        scheduled_at: record.scheduled_at || null,
        send_as: sendAs,
        audience_filters: normalizeAudienceFilters(record.audience_filters),
        audience_channel_filters: normalizeChannelAudienceFilters(record.audience_channel_filters),
        message_body: record.message_body ?? '',
        // Optional shorter variant for SMS/WhatsApp deliveries (CP-8018).
        // Blank = mobile channels fall back to message_body.
        mobile_message_body: record.mobile_message_body ?? '',
        purpose: Object.values(CAMPAIGN_PURPOSE).includes(record.purpose)
            ? record.purpose
            : CAMPAIGN_PURPOSE.MARKETING,
        include_cancelled_bookings: record.include_cancelled_bookings !== false,
        translate_to_recipient_lang: record.translate_to_recipient_lang === true,
        delivery_strategy: record.delivery_strategy || CAMPAIGN_STRATEGY.STOP_FIRST,
        delivery_reactions: normalizeDeliveryReactions(record.delivery_reactions),
        enabled: record.enabled !== false,
        atag: normalizeAtagList(record.atag),
        template: fkId(record.template),
        parent_campaign: fkId(record.parent_campaign),
        // Outgoing field is ``attachment_ids`` (list of ints). The
        // serializer maps it to the ``attachments`` M2M via source=.
        attachment_ids: Array.isArray(record.attachments)
            ? record.attachments.map((a) => a?.id).filter((v) => v != null)
            : [],
        ruleset: fkId(record.ruleset),
        delivery_methods: normalizeDeliveryMethods(record.delivery_methods).map(
            ({ id, ...rest }) => (id ? { id, ...rest } : rest),
        ),
    };
}

export function createBlankCampaign() {
    return {
        name: '',
        category: null,
        status: CAMPAIGN_STATUS.DRAFT,
        send_timing: CAMPAIGN_TIMING.NOW,
        scheduled_at: null,
        send_as: SEND_AS.PRIVATE_MESSAGE,
        // Soft-default filter: surface as a visible filter row in the
        // audience builder so the operator sees the gate and can remove it.
        // Prevents the "I sent the campaign to 50,000 people including
        // ones from 5 years ago" foot-gun by default. Same column key
        // works in both private_message and main_office mode.
        audience_filters: [
            { column: 'travel_period', operator: 'in_past', value: { unit: 'days', amount: 30 } },
        ],
        audience_channel_filters: [],
        message_body: '',
        mobile_message_body: '',
        // New campaigns start as Essential; Promotional is picked explicitly (and only where the operator has it).
        purpose: CAMPAIGN_PURPOSE.TRANSACTIONAL,
        // Promotional default: include cancelled-booking guests (opt-out).
        include_cancelled_bookings: true,
        translate_to_recipient_lang: false,
        translations: {},
        delivery_strategy: CAMPAIGN_STRATEGY.USE_ALL,
        delivery_reactions: [],
        enabled: true,
        atag: [],
        template: null,
        template_name: null,
        parent_campaign: null,
        attachments: [],
        ruleset: null,
        delivery_methods: [
            // Default channel tracks the default purpose (above) so the seeded
            // method always has a visible card in the picker. Essential's picker
            // shows only the composite "In-app Chat, else SMS/WhatsApp" — seeding
            // standalone CHAT here would leave an invisible enabled channel the
            // operator can't uncheck, which double-sends (chat + SMS/WhatsApp)
            // alongside the composite for guests without the app. See CP-8018.
            // Email off.
            {
                id: null,
                sequence: 0,
                method: DELIVERY_METHOD.CHAT_IF_AVAILABLE_OR_MOBILE_PHONE_NUMBER,
                receivers: 'user',
                label: '',
                enabled: true,
            },
        ],
    };
}

export async function fetchCampaignList(dataAPI) {
    const res = await dataAPI(GET_LIST, CAMPAIGN_RESOURCE, {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'updated_ts', order: 'DESC' },
    });
    if (!res.data) return [];
    return res.data.map(normalizeCampaignRow);
}

export async function fetchCampaignOne(dataAPI, id) {
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, { id });
    if (!res.data) return null;
    return normalizeCampaignRow(res.data);
}

export async function saveCampaign(dataAPI, record, isNew) {
    const payload = toApiPayload(record);
    if (!payload.name) {
        payload.name = 'Untitled campaign';
    }
    if (isNew) {
        const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, { data: payload });
        return normalizeCampaignRow(res?.data);
    }
    const res = await dataAPI(UPDATE, CAMPAIGN_RESOURCE, { id: record.id, data: payload });
    return normalizeCampaignRow(res?.data);
}

export async function deleteCampaignApi(dataAPI, id) {
    return dataAPI(DELETE, CAMPAIGN_RESOURCE, { id });
}

export async function duplicateCampaignApi(dataAPI, id) {
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, {
        data: {},
        urlSuffix: `${id}/duplicate/`,
    });
    return normalizeCampaignRow(res?.data);
}

export async function sendCampaignApi(dataAPI, id, targetStatus, options = {}) {
    // ``options`` may carry timing overrides chosen at the Send dialog so
    // the operator doesn't have to save the form first:
    //   - ``send_timing``  : 'now' | 'scheduled' (forced by backend based on
    //                        target_status when unset, but pass through for
    //                        explicit operator intent)
    //   - ``scheduled_at`` : ISO 8601 timestamp; required when
    //                        targetStatus === 'scheduled'
    const data = { target_status: targetStatus || CAMPAIGN_STATUS.SENT };
    if (options.send_timing) {
        data.send_timing = options.send_timing;
    }
    if (options.scheduled_at) {
        data.scheduled_at = options.scheduled_at;
    }
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, {
        data,
        urlSuffix: `${id}/send/`,
    });
    // The platform's dataProvider/httpClient does NOT throw on 4xx/5xx —
    // it resolves with ``{statusCode, data}`` regardless. Without this
    // check a precondition failure (e.g. "Add an email subject before
    // sending.") flows through ``normalizeCampaignRow`` and produces a
    // bogus "campaign" with no ``.status`` field, which the editor then
    // renders as "Campaign → undefined" in a *success* toast. Surface the
    // backend's ``message`` as a real Error so the caller's catch block
    // can show it instead.
    const statusCode = res?.statusCode;
    if (typeof statusCode === 'number' && statusCode >= 400) {
        const detail = res?.data?.message
            || res?.data?.detail
            || `send failed (HTTP ${statusCode})`;
        const err = new Error(detail);
        err.statusCode = statusCode;
        err.responseData = res?.data;
        throw err;
    }
    const campaign = normalizeCampaignRow(res?.data);
    // The backend's `send` action attaches a `send_summary` blob to the
    // response when target_status is "sent" (counts of successful/deduped/
    // errored Twilio posts plus a human-readable message). Preserve it so
    // the editor can show it in the snackbar instead of just "→ sent".
    const sendSummary = res?.data?.send_summary || null;
    return { campaign, sendSummary };
}

/**
 * Send a one-off **test** copy of the campaign so the operator can
 * proofread it — no attribution, billing, caps, or status transition.
 *
 * ``channel`` picks the destination:
 *   - ``'email'`` (default): emails ``email`` (or the operator's own
 *     address server-side); renders the full email incl. override/subject.
 *   - ``'phone'``: sends the shared body as SMS (US +1) or WhatsApp
 *     (international) to ``phone`` (E.164, e.g. ``+14155551234``).
 * Optional ``lang`` forces a translation language for the preview.
 *
 * Mirrors ``sendCampaignApi``'s error handling: the platform httpClient
 * never throws on 4xx/5xx, so we inspect ``statusCode`` and re-throw the
 * backend ``message`` as a real Error for the caller's catch block.
 */
export async function sendCampaignTestApi(dataAPI, id, { channel, email, phone, lang } = {}) {
    const data = {};
    if (channel) data.channel = channel;
    if (email) data.email = email;
    if (phone) data.phone = phone;
    if (lang) data.lang = lang;
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, {
        data,
        urlSuffix: `${id}/send_test/`,
    });
    const statusCode = res?.statusCode;
    if (typeof statusCode === 'number' && statusCode >= 400) {
        const detail = res?.data?.message
            || res?.data?.detail
            || `Test send failed (HTTP ${statusCode})`;
        const err = new Error(detail);
        err.statusCode = statusCode;
        throw err;
    }
    return res?.data || null;
}

/**
 * Paginated per-recipient delivery log for a sent/ongoing campaign.
 *
 * One row per Campaign_Attribution record joined with the message-log's
 * recipient. Used by the Results tab's Recipients table to let operators
 * see who got the campaign via which channel.
 *
 * Optional ``channel`` narrows to one delivery method
 * (chat | email | sms | whatsapp | airbnb | push). Optional ``q``
 * free-text-matches against first name / last name / email
 * (case-insensitive ``icontains`` on the server).
 */
export async function fetchCampaignRecipients(
    dataAPI,
    id,
    { page = 1, per_page = 50, channel = null, q = null } = {},
) {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('per_page', String(per_page));
    if (channel) qs.set('channel', channel);
    if (q && q.trim()) qs.set('q', q.trim());
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
        id,
        urlSuffix: `recipients/?${qs.toString()}`,
    });
    return res?.data || { count: 0, results: [] };
}

/**
 * Per-language recipient counts for the campaign's current audience.
 * Drives the translation preview modal so operators see "243 Spanish,
 * 12 French, 1 Mongolian" and decide which translations are worth
 * spot-checking. Returns top 8 by default; pass ``{ all: true }`` to
 * get the full long-tail list.
 *
 * Shape: ``{ languages: [{lang, count, label}], audience_mode }``.
 * ``audience_mode === 'channel'`` means announcement-mode campaign and
 * the list is empty (no per-user data); UI should hide preview.
 */
export async function fetchCampaignAudienceLanguages(dataAPI, id, { all = false } = {}) {
    const qs = all ? '?all=1' : '';
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
        id,
        urlSuffix: `audience_languages/${qs}`,
    });
    return res?.data || { languages: [], audience_mode: 'user' };
}

/**
 * Render the campaign body in one target language. Mirrors what a
 * recipient in that language would actually see at send time: source
 * body verbatim when source_lang == target_lang, the cached translation
 * when one exists, or a fresh LLM translation otherwise (cached server
 * side for the eventual send). Requires the campaign's
 * ``translate_to_recipient_lang`` toggle to be on (400 otherwise).
 *
 * Returns ``{lang, label, source_lang, body, was_cached, translated}``.
 */
export async function previewCampaignTranslation(dataAPI, id, lang) {
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, {
        data: { lang },
        urlSuffix: `${id}/preview_translation/`,
    });
    // 4xx surfaces the backend ``message`` so the caller's catch can show
    // it; match the sendCampaignApi pattern (the platform's dataProvider
    // never throws on non-2xx).
    const statusCode = res?.statusCode;
    if (typeof statusCode === 'number' && statusCode >= 400) {
        const detail = res?.data?.message || `Preview failed (HTTP ${statusCode})`;
        const err = new Error(detail);
        err.statusCode = statusCode;
        throw err;
    }
    return res?.data;
}

/**
 * Per-channel campaign daily-cap usage for the current tour operator.
 * Read by the Send dialog so the operator sees "X used / Y today" per
 * channel before committing, and the confirm button blocks when audience
 * would exceed remaining. ``channels[k].remaining`` is ``null`` for
 * unlimited channels (cap = 0 on Tour_Operator). Returns ``null`` if the
 * endpoint fails so the dialog can fall back gracefully (no cap row, but
 * confirm not blocked).
 */
export async function fetchCampaignCapUsage(dataAPI) {
    try {
        // List endpoint; id arg is unused by the GET_ONE handler when
        // urlSuffix is set, but our dataProvider still expects an id when
        // resource is singular. Use the detail=False action shape: GET_LIST
        // works since cap_usage is registered on the viewset.
        const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
            id: 'cap_usage',
            urlSuffix: '',
        });
        return res?.data || null;
    } catch (err) {
        console.warn('fetchCampaignCapUsage failed', err);
        return null;
    }
}

/**
 * Operator-facing dollar cost estimate for a saved campaign, read by the Send
 * step to show per-channel transport cost (Twilio list rates) + an upper-bound
 * MAU/engagement line ($0.05 per net-new active guest) before confirm.
 *
 * Returns the backend payload ``{currency, supported, channels, mau, total}``
 * or ``null`` on failure / for un-modeled modes so the panel hides the cost
 * rows gracefully (never blocks confirm — cost is informational, caps block).
 */
export async function fetchCampaignCostEstimate(dataAPI, id) {
    if (!id) return null;
    try {
        const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
            id,
            urlSuffix: 'cost_estimate/',
        });
        const data = res?.data || null;
        return data && data.supported ? data : null;
    } catch (err) {
        console.warn('fetchCampaignCostEstimate failed', err);
        return null;
    }
}

const _VALID_PREVIEW_MODES = new Set(Object.values(AUDIENCE_MODE));

/**
 * Preview audience for a saved campaign (uses its stored filters if no body).
 *
 * ``mode`` selects between the three audience surfaces (`user` /
 * `main_office` / `channel`) — the backend picks the matching column
 * registry and preview shape based on the body field, or falls back to
 * the campaign's stored ``send_as`` if ``mode`` is omitted.
 */
export async function previewAudienceSaved(
    dataAPI,
    id,
    {
        filters = null,
        page = 1,
        per_page = 20,
        mode = null,
        require_conversation = false,
        purpose = null,
        include_cancelled_bookings = null,
    } = {},
) {
    const data = {
        page,
        per_page,
    };
    if (filters != null) {
        data.filters = normalizeAudienceFilters(filters);
    }
    if (_VALID_PREVIEW_MODES.has(mode)) {
        data.mode = mode;
    }
    // Purpose + include-cancelled make the previewed count honor the
    // cancellation policy (Essential always excludes; Promotional opt-out).
    // Sent even though only the user-mode preview consumes them (channel /
    // main_office ignore them harmlessly).
    if (purpose != null) {
        data.purpose = purpose;
    }
    if (include_cancelled_bookings != null) {
        data.include_cancelled_bookings = include_cancelled_bookings;
    }
    if (require_conversation) {
        // Backend filters out rows without a live personal-tour
        // Twilio_Conversation so the count matches what a chat-only send
        // would actually reach. ``excluded_no_conversation`` comes back in
        // the response and feeds the explanation banner.
        data.require_conversation = true;
    }
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, {
        data,
        urlSuffix: `${id}/preview_audience/`,
    });
    return res?.data || { count: 0, results: [] };
}

/** Preview audience for an unsaved campaign (pass filters + mode explicitly). */
export async function previewAudienceUnsaved(
    dataAPI,
    {
        filters = [],
        page = 1,
        per_page = 20,
        mode = AUDIENCE_MODE.USER,
        require_conversation = false,
        purpose = null,
        include_cancelled_bookings = null,
    } = {},
) {
    const safeMode = _VALID_PREVIEW_MODES.has(mode) ? mode : AUDIENCE_MODE.USER;
    const data = {
        filters: normalizeAudienceFilters(filters),
        page,
        per_page,
        mode: safeMode,
    };
    if (purpose != null) {
        data.purpose = purpose;
    }
    if (include_cancelled_bookings != null) {
        data.include_cancelled_bookings = include_cancelled_bookings;
    }
    if (require_conversation) {
        data.require_conversation = true;
    }
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, {
        data,
        urlSuffix: 'preview_audience/',
    });
    return res?.data || { count: 0, results: [] };
}

/**
 * Fetch the filter / dropdown metadata for the audience builder and general
 * settings dropdowns.
 *
 * This intentionally uses GET_ONE (with an empty id + `urlSuffix`) rather than
 * GET_LIST. The `filter_options/` endpoint returns a plain dict without a
 * `count` field, and the GET_LIST branch of the data provider throws in that
 * case ("The total number of results is unknown…"), leaving the filter
 * registry empty and the "+ Add filter" button disabled.
 */
export async function fetchCampaignFilterOptions(dataAPI) {
    try {
        const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
            urlSuffix: 'filter_options/',
        });
        const body = res?.data;
        if (body && typeof body === 'object' && Array.isArray(body.columns)) {
            return body;
        }
    } catch (err) {
        console.warn('fetchCampaignFilterOptions failed', err);
    }
    return EMPTY_FILTER_REGISTRY;
}

export async function fetchCampaignReport(dataAPI, id, { fromDate, toDate } = {}) {
    if (id == null) return null;
    const params = { id, urlSuffix: 'report/' };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, params);
    return res?.data || null;
}

/**
 * Sends that didn't reach their recipient (newest first), each with the guest, channel, status label and reason.
 */
export async function fetchCampaignUndelivered(dataAPI, id, { fromDate, toDate, offset = 0, limit = 25 } = {}) {
    const qs = new URLSearchParams();
    qs.set('offset', String(offset));
    qs.set('limit', String(limit));
    if (fromDate) qs.set('from_date', fromDate);
    if (toDate) qs.set('to_date', toDate);
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
        id,
        urlSuffix: `undelivered/?${qs.toString()}`,
    });
    return res?.data || { count: 0, results: [] };
}

/**
 * Recipients the campaign deliberately left out (replied STOP on WhatsApp, or the number has no WhatsApp), newest
 * first, so the operator can reach them another way. Same row shape as ``fetchCampaignUndelivered``.
 */
export async function fetchCampaignNotSent(dataAPI, id, { offset = 0, limit = 25 } = {}) {
    const qs = new URLSearchParams();
    qs.set('offset', String(offset));
    qs.set('limit', String(limit));
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, {
        id,
        urlSuffix: `not_sent/?${qs.toString()}`,
    });
    return res?.data || { count: 0, results: [] };
}

/**
 * Before sending: how many recipients won't get the campaign on WhatsApp (replied STOP / no WhatsApp).
 * ``{enabled, counts: {whatsapp_opted_out, no_whatsapp} | null}``; ``enabled`` false while the operator's switch is
 * off (an older backend answers 404: treated the same).
 */
export async function fetchCampaignWhatsAppUnreachable(dataAPI, id) {
    const res = await dataAPI(GET_ONE, CAMPAIGN_RESOURCE, { id, urlSuffix: 'whatsapp_unreachable/' });
    const data = res?.data;
    if (!data || typeof data.enabled !== 'boolean') return { enabled: false, counts: null };
    return data;
}

/**
 * Send step: is this Essential campaign's saved text promotional? (backend rollout phase 3; the model reads each
 * version of the text once, so this can take a few seconds the first time.) ``{enabled: false}`` unless the
 * operator's check blocks; else ``{enabled, verdict, blocked, reason, phrases, message, overridden, appealed,
 * can_override}``. Any failure answers ``{enabled: false}``: the backend checks again at send, so the step never
 * waits on this.
 */
export async function checkCampaignPromo(dataAPI, id) {
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, { data: {}, urlSuffix: `${id}/promo_check/` });
    const data = res?.data;
    if (res?.statusCode >= 400 || !data || typeof data.enabled !== 'boolean') return { enabled: false };
    return data;
}

async function _promoAction(dataAPI, id, action, data, fallback) {
    const res = await dataAPI(CREATE, CAMPAIGN_RESOURCE, { data, urlSuffix: `${id}/${action}/` });
    if (typeof res?.statusCode === 'number' && res.statusCode >= 400) {
        throw new Error(res?.data?.message || res?.data?.detail || fallback);
    }
    return res?.data;
}

/** Contest a promotional verdict: emails TourOptima with the operator's optional note. Answers like the check. */
export function appealCampaignPromo(dataAPI, id, note) {
    return _promoAction(dataAPI, id, 'promo_appeal', { note: note || '' }, "Couldn't send your request.");
}

/** Support only (the help@curatedplanet.com login): let this version of the text through. */
export function overrideCampaignPromo(dataAPI, id) {
    return _promoAction(dataAPI, id, 'promo_override', {}, "Couldn't allow this campaign.");
}
